import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { optionalGender } from '@anihis/shared/src/functions/optional-gender';
import {
  AnimalDto,
  AnimalsService,
  Gender,
  PaginationResult,
  UsersService,
} from '@anihis/core/data-access';
import {
  patchState,
  signalStore,
  withMethods,
  withState,
  withHooks,
} from '@ngrx/signals';
import { explicitEffect } from 'ngxtension/explicit-effect';
import { TabsStateService } from '../../shared/services/tabs-state.service';
import { DateTime } from 'luxon';
import {
  finalize,
  switchMap,
  take,
  tap,
  catchError,
  map,
} from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { LoadingStatus } from '../../shared/enums/loading';
import { SelectClinicStore } from '../../shared/component/select-clinic/select-clinic.store';
import { AllFilters } from '../../shared/models/all-filter.model';
import { initialFilters } from '../../shared/models/all-initial-filters';
import { AddEditOwnerDialogComponent } from '../../shared/component/add-edit-owner-dialog/add-edit-owner-dialog.component';
import { AddEditReportDialogComponent } from './add-edit-report-dialog/add-edit-report-dialog.component';
import { ImagePreviewDialogComponent } from '../../shared/component/image-preview-dialog/image-preview-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { TourStore } from '../../shared/component/tour/tour.store';
import { ConfirmationDialogComponent } from '../../shared/component/confirmation-dialog/confirmation-dialog.component';
import { ShellStore } from '../../shell/shell.store';
import { TranslocoService } from '@jsverse/transloco';
import { PORTAL_DIALOG } from '../../shared/constants/dialog.constants';
import { ReportSubscriptionGuardService } from '../../shared/services/report-subscription-guard.service';
import { environment } from 'apps/anihis-portal/src/environments/environment';

export type CardsQuery = {
  pageNumber: number;
  pageSize: number;
  filters: AllFilters;
  pagination: PaginationResult | undefined;
};

type CardsState = {
  data: AnimalDto[] | undefined;
  animalUid: string | undefined;
  status: LoadingStatus | undefined;
  statusSearch: LoadingStatus | undefined;
  pagination: PaginationResult | undefined;
  query: CardsQuery;
};

const emptyQuery: CardsQuery = {
  pageNumber: 1,
  pageSize: 15,
  filters: initialFilters,
  pagination: undefined,
};

const initialState: CardsState = {
  data: undefined,
  animalUid: undefined,
  status: undefined,
  statusSearch: undefined,
  pagination: undefined,
  query: emptyQuery,
};

export const CardsStore = signalStore(
  withState(initialState),
  withMethods(
    (
      store,
      animalsService = inject(AnimalsService),
      httpClient = inject(HttpClient),
      usersService = inject(UsersService),
      dialog = inject(MatDialog),
      tour = inject(TourStore),
      shellStore = inject(ShellStore),
      transloco = inject(TranslocoService),
      reportSubscriptionGuard = inject(ReportSubscriptionGuardService),
      clinicsStore = inject(SelectClinicStore),
    ) => {
      return {
        exportIntakeFormPdf(): Observable<void> {
          const clinicUid = clinicsStore.selectedClinicUid() ?? undefined;
          const lang = transloco.getActiveLang() || 'sr-Latn';

          return animalsService.animalsIntakeFormPdfGet(clinicUid, lang).pipe(
            tap((blob: Blob) => {
              const url = URL.createObjectURL(blob);
              window.open(url, '_blank');
              setTimeout(() => URL.revokeObjectURL(url), 60_000);
            }),
            map(() => undefined),
          );
        },

        setAnimalUid(animalUid: string) {
          patchState(store, { animalUid: animalUid });
        },

        fetchCards() {
          const { filters, pageSize } = store.query();

          patchState(store, { status: LoadingStatus.Loading });
          return animalsService
            .animalsGet(filters.search, filters.includeDeceased, 1, pageSize)
            .pipe(
              tap((res) => {
                patchState(store, {
                  data: res.animals,
                  pagination: {
                    pageNumber: res.pageNumber,
                    pageSize: res.pageSize,
                    totalCount: res.totalCount,
                    totalPages: res.totalPages,
                    hasNextPage: res.hasNextPage,
                    hasPreviousPage: res.hasPreviousPage,
                  },
                });
              }),
              finalize(() => {
                patchState(store, {
                  status: LoadingStatus.Loaded,
                  statusSearch: LoadingStatus.Loaded,
                });
              }),
            );
        },

        toggleDeceased(on: boolean) {
          patchState(store, {
            data: undefined,
            pagination: undefined,
            statusSearch: LoadingStatus.Loading,
            query: {
              ...store.query(),
              pageNumber: 1,
              filters: {
                ...store.query().filters,
                includeDeceased: on,
              },
            },
          });
          this.fetchCards().subscribe();
        },

        updateSearch(search: string) {
          patchState(store, {
            statusSearch: LoadingStatus.Loading,
            query: {
              ...store.query(),
              pageNumber: 1,
              filters: {
                ...store.query().filters,
                search,
              },
            },
          });
          this.fetchCards().subscribe();
        },

        appendData(newCards: AnimalDto[], pagination: PaginationResult) {
          const currentData = store.data() ?? [];
          const currentUids = new Set(currentData.map((a) => a.animalUid));
          const uniqueNewCards = newCards.filter(
            (a) => !currentUids.has(a.animalUid),
          );

          patchState(store, {
            data: [...currentData, ...uniqueNewCards],
            status: LoadingStatus.Loaded,
            pagination: {
              pageNumber: pagination.pageNumber,
              pageSize: pagination.pageSize,
              totalCount: pagination.totalCount,
              totalPages: pagination.totalPages,
              hasNextPage: pagination.hasNextPage,
              hasPreviousPage: pagination.hasPreviousPage,
            },
          });
        },

        loadMoreData() {
          const { filters, pageSize } = store.query();
          const nextPage = store.pagination()?.pageNumber! + 1;
          patchState(store, { status: LoadingStatus.Loading });
          return animalsService
            .animalsGet(
              filters.search,
              filters.includeDeceased,
              nextPage,
              pageSize,
            )
            .pipe(
              tap((res) => {
                this.appendData(res.animals ?? [], {
                  pageNumber: res.pageNumber ?? nextPage,
                  pageSize: res.pageSize ?? pageSize,
                  totalCount: res.totalCount ?? 0,
                  totalPages: res.totalPages ?? nextPage,
                  hasNextPage: res.hasNextPage ?? false,
                  hasPreviousPage: res.hasPreviousPage ?? false,
                });
              }),
              finalize(() => {
                patchState(store, {
                  status: LoadingStatus.Loaded,
                  statusSearch: LoadingStatus.Loaded,
                });
              }),
            );
        },

        sanitize<T extends Record<string, any>>(data: T): T {
          return Object.fromEntries(
            Object.entries(data).map(([k, v]) => [
              k,
              v === undefined ? null : v,
            ]),
          ) as T;
        },

        createNewOwnerAndAnimal(dataForms: any) {
          const formOwner = this.sanitize(dataForms.formOwner);
          const formAnimal = this.sanitize(dataForms.formAnimal);
          const selectedOwnerUid = dataForms.selectedOwnerUid;

          patchState(store, { status: LoadingStatus.Loading });

          // If an existing owner was selected, just create the animal
          if (selectedOwnerUid) {
            return this.createNewAnimal(formAnimal, selectedOwnerUid).pipe(
              finalize(() => {
                patchState(store, {
                  status: LoadingStatus.Loaded,
                  statusSearch: LoadingStatus.Loaded,
                });
              }),
            );
          }

          // Otherwise, create both owner and animal
          return usersService
            .usersOwnersPostForm(
              formOwner.email,
              formOwner.mobileNumber,
              undefined,
              undefined,
              formOwner.phoneNumber,
              formOwner.address,
              formOwner.city,
              formOwner.postalCode,
              formOwner.country,
              undefined,
              undefined,
              undefined,
              undefined,
              formOwner.firstName,
              formOwner.lastName,
              optionalGender(formOwner.gender),
              formOwner.discount,
            )
            .pipe(
              switchMap((x) => this.createNewAnimal(formAnimal, x.uid)),
              finalize(() => {
                patchState(store, {
                  status: LoadingStatus.Loaded,
                  statusSearch: LoadingStatus.Loaded,
                });
              }),
            );
        },

        toUTCISOString(
          date: Date | string | null | undefined,
        ): string | undefined {
          if (!date) return undefined;
          return DateTime.fromJSDate(new Date(date))
            .startOf('day')
            .toUTC()
            .toISO()!;
        },

        createNewAnimal(data: any, userUid: string | undefined) {
          patchState(store, {
            status: LoadingStatus.Loading,
            statusSearch: LoadingStatus.Loading,
          });

          const formData = new FormData();
          if (userUid) formData.append('ownerUid', userUid);
          if (data.name) formData.append('name', data.name);
          if (data.breed) formData.append('breedUid', data.breed);
          if (data.gender) formData.append('gender', data.gender);
          if (data.colors?.length)
            data.colors.forEach((c: any) => formData.append('color', c.name));
          if (data.dateOfBirth) {
            formData.append(
              'birthDateTime',
              this.toUTCISOString(data.dateOfBirth)!,
            );
          } else if (data.birthDateText) {
            formData.append('birthDateText', data.birthDateText);
          }
          if (data.microchip) formData.append('microchip', data.microchip);
          if (data.passportNumber)
            formData.append('passportNumber', data.passportNumber);
          if (data.passportDate)
            formData.append(
              'passportDateTime',
              this.toUTCISOString(data.passportDate)!,
            );
          if (data.vuIssuingPassports)
            formData.append('vIIssuesAPassport', data.vuIssuingPassports);
          if (data.warning) formData.append('warning', data.warning);
          if (data.markingDate)
            formData.append(
              'markingDateTimeUtc',
              this.toUTCISOString(data.markingDate)!,
            );
          if (data.pedigree) formData.append('pedigree', data.pedigree);
          if (data.sterilized !== undefined)
            formData.append('sterilized', String(data.sterilized));
          if (data.dateOfSterilization)
            formData.append(
              'sterilizedDateTimeUtc',
              this.toUTCISOString(data.dateOfSterilization)!,
            );
          if (typeof data.exitusLetalis === 'boolean')
            formData.append('exitusLetalis', String(data.exitusLetalis));

          return httpClient
            .post(`${environment.apiUrl}/Animals`, formData, {
              reportProgress: true,
              observe: 'body',
            })
            .pipe(
              tap((newAnimal: any) => {
                // Fetch the complete animal data with owner info
                animalsService.animalsUidGet(newAnimal.uid).subscribe({
                  next: (fullAnimal) => {
                    if (fullAnimal.animal) {
                      if (fullAnimal.animal.owner?.uid) {
                        // Fetch complete owner data
                        usersService
                          .usersOwnersUidGet(fullAnimal.animal.owner.uid)
                          .subscribe({
                            next: (ownerResponse) => {
                              if (ownerResponse.owner) {
                                // Update animal with complete owner data
                                const updatedAnimal = {
                                  ...fullAnimal.animal,
                                  owner: ownerResponse.owner,
                                };
                                const current = store.data() ?? [];
                                patchState(store, {
                                  data: [...current, updatedAnimal],
                                });
                              }
                            },
                            error: (err) => {
                              console.error('Failed to fetch owner data', err);
                              // Still add animal without complete owner data
                              const current = store.data() ?? [];
                              patchState(store, {
                                data: [...current, fullAnimal.animal!],
                              });
                            },
                          });
                      } else {
                        // No owner, just add animal
                        const current = store.data() ?? [];
                        patchState(store, {
                          data: [...current, fullAnimal.animal!],
                        });
                      }
                    }
                  },
                  error: (err) => {
                    console.error('Failed to fetch new animal data', err);
                  },
                });
                return undefined;
              }),
              catchError(() => {
                patchState(store, {
                  status: LoadingStatus.Loaded,
                  statusSearch: LoadingStatus.Loaded,
                });
                return of(null);
              }),
            );
        },

        editNewAnimal(data: any, skipOwnerFetch: boolean = false) {
          patchState(store, {
            status: LoadingStatus.Loading,
            statusSearch: LoadingStatus.Loading,
          });

          const formData = new FormData();
          formData.append('uid', data.animaUid);
          if (data.ownerUid) {
            formData.append('ownerUid', data.ownerUid);
          }
          if (data.name) {
            formData.append('name', data.name);
          }
          if (data.breed) {
            formData.append('breedUid', data.breed);
          }

          if (data.description) {
            formData.append('description', data.description);
          }
          if (data.gender) {
            formData.append('gender', data.gender);
          }
          if (data.colors?.length) {
            data.colors.forEach((c: any) => formData.append('color', c.name));
          }
          if (data.dateOfBirth) {
            formData.append(
              'birthDateTime',
              this.toUTCISOString(data.dateOfBirth)!,
            );
          } else if (data.birthDateText) {
            formData.append('birthDateText', data.birthDateText);
          }
          if (data.microchip) {
            formData.append('microchip', data.microchip);
          }
          if (data.passportNumber) {
            formData.append('passportNumber', data.passportNumber);
          }
          if (data.passportDate) {
            formData.append(
              'passportDateTime',
              this.toUTCISOString(data.passportDate)!,
            );
          }
          if (data.vuIssuingPassports) {
            formData.append('vIIssuesAPassport', data.vuIssuingPassports);
          }
          if (data.warning) {
            formData.append('warning', data.warning);
          }
          if (data.markingDate) {
            formData.append(
              'markingDateTimeUtc',
              this.toUTCISOString(data.markingDate)!,
            );
          }
          if (data.pedigree) {
            formData.append('pedigree', data.pedigree);
          }
          if (data.sterilized !== undefined) {
            formData.append('sterilized', String(data.sterilized));
          }
          if (data.dateOfSterilization) {
            formData.append(
              'sterilizedDateTimeUtc',
              this.toUTCISOString(data.dateOfSterilization)!,
            );
          }
          if (typeof data.exitusLetalis === 'boolean') {
            formData.append('exitusLetalis', String(data.exitusLetalis));
          }

          httpClient
            .put(`${environment.apiUrl}/Animals/${data.animaUid}`, formData, {
              reportProgress: true,
              observe: 'body',
            })
            .pipe(
              switchMap(() => {
                // Fetch updated animal data from server
                return animalsService.animalsUidGet(data.animaUid);
              }),
              switchMap((updatedAnimal: any) => {
                if (updatedAnimal.animal?.owner?.uid) {
                  // Fetch complete owner data
                  return usersService
                    .usersOwnersUidGet(updatedAnimal.animal.owner.uid)
                    .pipe(
                      map((ownerResponse: any) => ({
                        animal: updatedAnimal.animal,
                        owner: ownerResponse.owner,
                      })),
                      catchError(() =>
                        of({
                          animal: updatedAnimal.animal,
                          owner: updatedAnimal.animal?.owner,
                        }),
                      ),
                    );
                } else {
                  return of({ animal: updatedAnimal.animal, owner: null });
                }
              }),
              tap(({ animal, owner }) => {
                if (animal) {
                  const updatedAnimal = owner ? { ...animal, owner } : animal;
                  const includeDeceased = store.query().filters.includeDeceased;
                  patchState(store, (state) => ({
                    data:
                      !includeDeceased && updatedAnimal.exitusLetalis
                        ? state.data?.filter(
                            (a) => a.animalUid !== data.animaUid,
                          )
                        : state.data?.map((a) =>
                            a.animalUid === data.animaUid ? updatedAnimal : a,
                          ),
                    status: LoadingStatus.Loaded,
                    statusSearch: LoadingStatus.Loaded,
                  }));
                }
              }),
              catchError(() => {
                patchState(store, {
                  status: LoadingStatus.Loaded,
                  statusSearch: LoadingStatus.Loaded,
                });
                return of(null);
              }),
            )
            .subscribe();
        },

        editOwner(dataForms: any) {
          patchState(store, { status: LoadingStatus.Loading });
          const newFile: File | null = dataForms.formImageData ?? null;
          return usersService
            .usersOwnersUidPutForm(
              dataForms.formOwner.ownerUid,
              undefined,
              dataForms.formOwner.warning,
              newFile ?? undefined,
              newFile ? undefined : dataForms.formOwner.profilePictureUid,
              dataForms.formOwner.email,
              dataForms.formOwner.mobileNumber,
              dataForms.ownerData?.contact?.additionalMobileNumbers,
              dataForms.ownerData?.contact?.dutyMobileNumber,
              dataForms.formOwner.phoneNumber,
              dataForms.formOwner.address,
              dataForms.formOwner.city,
              dataForms.formOwner.postalCode,
              dataForms.formOwner.country,
              undefined,
              undefined,
              undefined,
              undefined,
              dataForms.formOwner.firstName,
              dataForms.formOwner.lastName,
              optionalGender(dataForms.formOwner.gender),
              dataForms.formOwner.discount,
              dataForms.formOwner.description ?? undefined,
              dataForms.formOwner.nickname ?? undefined,
            )
            .pipe(
              switchMap(() => {
                // Fetch updated owner data from server
                return usersService
                  .usersOwnersUidGet(dataForms.formOwner.ownerUid)
                  .pipe(
                    tap((updatedOwner) => {
                      if (updatedOwner.owner) {
                        // Update owner data in local state
                        patchState(store, (state) => ({
                          data: state.data?.map((animal) => {
                            if (
                              animal.owner?.uid === dataForms.formOwner.ownerUid
                            ) {
                              return {
                                ...animal,
                                owner: updatedOwner.owner,
                              };
                            }
                            return animal;
                          }),
                        }));
                        this.editNewAnimal(dataForms.formAnimal, true);
                      }
                      return undefined;
                    }),
                  );
              }),
              finalize(() => {
                patchState(store, {
                  status: LoadingStatus.Loaded,
                  statusSearch: LoadingStatus.Loaded,
                });
              }),
            );
        },

        openAddNewOwnerDialog() {
          dialog.open(AddEditOwnerDialogComponent, {
            ...PORTAL_DIALOG.medium,
            autoFocus: false,
            disableClose: true,
            data: {
              saveFn: (result: any) => this.createNewOwnerAndAnimal(result),
            },
          });
        },

        previewImage(url: string) {
          const dialogRef = dialog.open(ImagePreviewDialogComponent, {
            data: { url },
            panelClass: 'image-dialog-panel',
            backdropClass: 'image-dialog-backdrop',
            hasBackdrop: true,
            disableClose: false,
          });

          dialogRef.afterClosed().subscribe((result) => {
            // if (result?.action === 'remove') {
            //   this.profileImage = null;
            //   this.uploadedFile = null;
            //   this.files = [];
            // } else if (result?.action === 'replace') {
            //   this.replaceImage();
            // }
          });
        },

        openAddReportDialog(data?: { owner?: { unpaidExpenses?: number } }) {
          reportSubscriptionGuard
            .confirmReportAccess()
            .pipe(take(1))
            .subscribe((allowed) => {
              if (!allowed) return;
              this.openAddReportDialogAfterSubscriptionCheck(data);
            });
        },

        openAddReportDialogAfterSubscriptionCheck(data?: {
          owner?: { unpaidExpenses?: number };
        }) {
          const unpaid = Number(data?.owner?.unpaidExpenses ?? 0);
          const limit = Number(shellStore.debt()?.debtLimit ?? 0);

          const fmt0 = (v: number) =>
            new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(
              v,
            );

          const hasLimit = Number.isFinite(limit) && limit > 0;
          const nearThreshold = hasLimit ? limit * 0.9 : Infinity;

          if (hasLimit && unpaid > limit) {
            const title = transloco.translate('debt.overLimit.title');
            const text = transloco.translate('debt.overLimit.text', {
              debt: fmt0(unpaid),
              limit: fmt0(limit),
            });

            const ref = dialog.open(ConfirmationDialogComponent, {
              width: '400px',
              data: { title, text },
              disableClose: true,
            });

            ref
              .afterClosed()
              .pipe(take(1))
              .subscribe((res) => {
                if (res?.confirmed) this.reportDialog(data);
              });
          } else if (hasLimit && unpaid >= nearThreshold && unpaid < limit) {
            const remaining = Math.max(0, limit - unpaid);
            const percent = Math.round((unpaid / limit) * 100);

            const title = transloco.translate('debt.nearLimit.title');
            const text = transloco.translate('debt.nearLimit.text', {
              debt: fmt0(unpaid),
              limit: fmt0(limit),
              remaining: fmt0(remaining),
              percent: String(percent),
            });

            const ref = dialog.open(ConfirmationDialogComponent, {
              width: '400px',
              data: { title, text },
              disableClose: true,
            });

            ref
              .afterClosed()
              .pipe(take(1))
              .subscribe((res) => {
                if (res?.confirmed) this.reportDialog(data);
              });
          } else {
            this.reportDialog(data);
          }
        },

        reportDialog(data?: any) {
          dialog.open(AddEditReportDialogComponent, {
            ...PORTAL_DIALOG.wide,
            height: '85vh',
            disableClose: true,
            data: { ...data },
            autoFocus: false,
          });
        },

        openEditDialog(data: any) {
          dialog.open(AddEditOwnerDialogComponent, {
            ...PORTAL_DIALOG.medium,
            data: { ...data, saveFn: (result: any) => this.editOwner(result) },
            autoFocus: false,
            disableClose: true,
          });
        },
        startDashboardTour() {
          tour.startTour([
            {
              elementId: 'add-card',
              title: 'Add Owner and Animal Card',
              message:
                'By clicking “Add Owner and Animal Card”, a form will appear. Once the form is completed and submitted, a new owner and an animal card will be created.',
              offsetY: 50,
              offsetX: -350,
            },

            {
              elementId: 'card-table',
              title: 'Cards Board',
              message:
                'The table displays all animal cards along with their basic information.',
              offsetY: 50,
              offsetX: 250,
            },

            {
              elementId: 'card-actions',
              title: 'Card Actions',
              message:
                'There are three available options. Clicking the left icon opens a form for creating a report. Clicking the middle icon opens a form for editing the owner’s information and the animal’s details. Clicking the right icon opens the selected animal card, where you can view all information about the animal.',
              offsetY: 50,
              offsetX: -350,
            },
          ]);
        },
      };
    },
  ),
  withHooks({
    onInit(store, tabsService = inject(TabsStateService)) {
      const already = tabsService.hasRefreshOccurred('Cards');
      if (!already) {
        store.fetchCards().subscribe();
      }

      // react to explicit activation requests for the 'Cards' tab
      explicitEffect([tabsService.getRefreshCount('Cards')], ([cnt]) => {
        if (typeof cnt === 'number' && cnt > 0) {
          store.fetchCards().subscribe();
        }
      });
    },
  }),
);
